import {
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnInit,
} from '@angular/core';

import {
  createAnchorName,
  createPopoverId,
  supportsPopoverHint,
} from '../shared/anchor-positioning';
import {
  TwOverlayFallbackHandle,
  TwOverlayFallbackService,
} from '../shared/cdk-fallback';
import { isNativePositioningAvailable } from '../shared/positioning-strategy';
import { TwPopoverPlacement } from '../shared/types';

import { TW_TOOLTIP_STYLESHEET } from './tw-tooltip-styles';

/**
 * Module-level guard — the tooltip stylesheet is injected into
 * `document.head` at most once per page load. Multiple directive
 * instances share a single `<style>` element.
 *
 * A module-level boolean is preferable to DI tokens here: the
 * stylesheet is a pure side-effect resource with no per-injector
 * variance, and guarding at the class level avoids pulling the
 * Angular injector into the style-injection path.
 */
let styleInjected = false;

/**
 * Internal delay machine states. Kept as a union for self-documenting
 * transitions instead of a pair of booleans that can drift out of sync.
 */
type TooltipState = 'idle' | 'pending-show' | 'visible' | 'pending-hide';

/**
 * `[twTooltip]` — compact hover/focus hint directive (TWS-122 Wave 2).
 *
 * Applied directly to any interactive host:
 *
 * ```html
 * <button [twTooltip]="'Delete installation'" twTooltipPlacement="top">
 *   <twi-icon name="trash" />
 * </button>
 * ```
 *
 * ### Pooled-per-host DOM
 *
 * On first interaction the directive creates ONE
 * `<div popover="hint" role="tooltip">` per host and attaches it
 * to `document.body`. That element is reused across subsequent
 * shows for this host's lifetime. A single global pool was
 * considered and rejected: two tooltips triggered in quick
 * succession would race over the shared element's text content,
 * and the host's `aria-describedby` would need to be rewritten on
 * every interaction.
 *
 * ### Lazy creation
 *
 * The tooltip DOM is NOT created in the constructor. `OnInit`
 * sets up `aria-describedby` using the pre-generated id (so
 * assistive tech knows help text is available even before the
 * user hovers), but the actual `<div>` is materialised on first
 * mouseenter/focusin. This keeps the DOM clean for the vast
 * majority of hosts whose tooltips are never triggered.
 *
 * ### `popover="hint"` vs `popover="auto"`
 *
 * The directive prefers `popover="hint"` where supported — hints
 * auto-dismiss when another hint opens, which is the expected
 * tooltip behaviour. Older browsers (shipped `[popover]` but not
 * the `hint` variant, e.g. Chrome 114–133) fall back to
 * `popover="auto"` where only one auto-popover can be open at a
 * time; a small behavioural difference (auto-popovers close on
 * outside click rather than on next-hint-open) but visually
 * indistinguishable for our use case.
 *
 * CDK-based positioning fallback for browsers without CSS anchor
 * positioning lands in Wave 3.
 *
 * ### Show / hide delays
 *
 * Keyboard focus uses the same `twTooltipShowDelay` as hover
 * rather than the immediate show that WAI-ARIA APG suggests.
 * Reason: users who Tab through a form quickly would see tooltips
 * flash open and closed on every focus stop, which is visually
 * noisy. A short 300 ms delay hides that. If a keyboard-focus-
 * immediate mode is required by a specific consumer, it can be
 * added as a boolean input in a future iteration.
 */
@Directive({
  selector: '[twTooltip]',
  standalone: true,
})
export class TwTooltipDirective implements OnInit {
  // ---------------------------------------------------------------
  // Public signal inputs
  // ---------------------------------------------------------------

  /** Tooltip message. Required — there's no point wiring up the directive without one. */
  readonly twTooltip = input.required<string>();

  /** Preferred anchor side. Matches the popover + dropdown placement type. */
  readonly twTooltipPlacement = input<TwPopoverPlacement>('top');

  /** Show delay (ms) — applied to both hover and focus to keep transitions consistent. */
  readonly twTooltipShowDelay = input<number>(300);

  /** Hide delay (ms) — keeps the tooltip available across small pointer excursions. */
  readonly twTooltipHideDelay = input<number>(100);

  /**
   * Disables the tooltip entirely. If flipped to `true` while
   * the tooltip is visible or has a pooled DOM element, both are
   * torn down.
   */
  readonly twTooltipDisabled = input<boolean>(false);

  // ---------------------------------------------------------------
  // DI
  // ---------------------------------------------------------------

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly fallback = inject(TwOverlayFallbackService);

  // ---------------------------------------------------------------
  // Instance state
  // ---------------------------------------------------------------

  /** Id assigned up front so `aria-describedby` can reference it immediately. */
  private readonly tooltipId = createPopoverId();

  /** Anchor-name shared between host and pooled tooltip. */
  private readonly anchorName = createAnchorName();

  /** Runtime path — memoised. */
  private readonly useNative = isNativePositioningAvailable();

  /** Active CDK fallback handle while the tooltip is visible in fallback mode. */
  private fallbackHandle: TwOverlayFallbackHandle | null = null;

  /** Lazily-created pooled tooltip DOM. `null` until first show. */
  private tooltipEl: HTMLElement | null = null;

  /** Delay machine. */
  private state: TooltipState = 'idle';

  /** Timer handles for show + hide delays. */
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  /** Tracks whether the host is still hovered / focused when a timer fires. */
  private hovered = false;
  private focused = false;

  /** Bound handler refs so we can remove them on destroy. */
  private readonly onMouseEnter = (): void => this.requestShow('hover');
  private readonly onMouseLeave = (): void => this.requestHide('hover');
  private readonly onFocusIn = (): void => this.requestShow('focus');
  private readonly onFocusOut = (): void => this.requestHide('focus');
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.state === 'visible') {
      this.hideImmediate();
    }
  };

  // ---------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------

  ngOnInit(): void {
    TwTooltipDirective.ensureStylesInjected();

    const hostEl = this.host.nativeElement;

    // Describedby is set up front so AT users know the help
    // exists. The id references the tooltip element that will
    // materialise on first interaction — until then the element
    // doesn't exist in the DOM, but that's harmless: screen
    // readers that fail to resolve the id just fall through to
    // the host's own label, which is exactly what they'd do if
    // the directive weren't present.
    const existingDescribedBy = hostEl.getAttribute('aria-describedby');
    const describedBy = existingDescribedBy
      ? `${existingDescribedBy} ${this.tooltipId}`
      : this.tooltipId;
    hostEl.setAttribute('aria-describedby', describedBy);

    // Set `anchor-name` on the HOST. Dashed-ident literal — NOT a
    // `var()` wrap. Same pattern as the dropdown trigger's
    // inline-style write.
    hostEl.style.setProperty('anchor-name', this.anchorName);

    this.wireHandlers();

    // Effects run in `ngOnInit` (not the constructor) because
    // they read signal inputs that only populate after binding —
    // the `{ injector }` option threads the element injector
    // through so the effect still cleans up with the directive.

    // React to `disabled` flipping — teardown tooltip + detach
    // listeners when disabled goes true.
    effect(
      () => {
        if (this.twTooltipDisabled()) {
          this.unwireHandlers();
          this.hideImmediate();
          this.destroyPooledElement();
        } else {
          this.wireHandlers();
        }
      },
      { injector: this.injector },
    );

    // Keep the pooled element's text in sync with the message input.
    effect(
      () => {
        const message = this.twTooltip();
        if (this.tooltipEl) {
          this.tooltipEl.textContent = message;
        }
      },
      { injector: this.injector },
    );

    // Keep the pooled element's placement attribute in sync.
    effect(
      () => {
        const placement = this.twTooltipPlacement();
        if (this.tooltipEl) {
          this.tooltipEl.setAttribute('data-placement', placement);
        }
      },
      { injector: this.injector },
    );

    this.destroyRef.onDestroy(() => {
      this.unwireHandlers();
      this.hideImmediate();
      this.destroyPooledElement();
      // Leave `aria-describedby` as-is — the host may still be
      // in the DOM after the directive destroys (e.g. structural
      // directive toggle) and trimming the describedby mid-flight
      // risks stripping IDs from sibling directives.
    });
  }

  // ---------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------

  private handlersWired = false;

  private wireHandlers(): void {
    if (this.handlersWired) return;
    if (this.twTooltipDisabled()) return;

    const el = this.host.nativeElement;
    el.addEventListener('mouseenter', this.onMouseEnter);
    el.addEventListener('mouseleave', this.onMouseLeave);
    el.addEventListener('focusin', this.onFocusIn);
    el.addEventListener('focusout', this.onFocusOut);
    el.addEventListener('keydown', this.onKeyDown);
    this.handlersWired = true;
  }

  private unwireHandlers(): void {
    if (!this.handlersWired) return;
    const el = this.host.nativeElement;
    el.removeEventListener('mouseenter', this.onMouseEnter);
    el.removeEventListener('mouseleave', this.onMouseLeave);
    el.removeEventListener('focusin', this.onFocusIn);
    el.removeEventListener('focusout', this.onFocusOut);
    el.removeEventListener('keydown', this.onKeyDown);
    this.handlersWired = false;
  }

  // ---------------------------------------------------------------
  // Delay machine transitions
  // ---------------------------------------------------------------

  private requestShow(source: 'hover' | 'focus'): void {
    if (source === 'hover') this.hovered = true;
    else this.focused = true;

    if (this.twTooltipDisabled()) return;

    this.clearHideTimer();
    if (this.state === 'visible' || this.state === 'pending-show') return;

    this.state = 'pending-show';
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      // Recheck — the user may have left the host during the delay.
      if (!this.hovered && !this.focused) {
        this.state = 'idle';
        return;
      }
      this.showNow();
    }, this.twTooltipShowDelay());
  }

  private requestHide(source: 'hover' | 'focus'): void {
    if (source === 'hover') this.hovered = false;
    else this.focused = false;

    this.clearShowTimer();
    if (this.state === 'idle' || this.state === 'pending-hide') return;

    this.state = 'pending-hide';
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      // Recheck — pointer / focus may have returned during the delay.
      if (this.hovered || this.focused) {
        this.state = 'visible';
        return;
      }
      this.hideNow();
    }, this.twTooltipHideDelay());
  }

  private showNow(): void {
    const el = this.ensurePooledElement();
    if (!el) return;
    el.textContent = this.twTooltip();
    el.setAttribute('data-placement', this.twTooltipPlacement());

    if (this.useNative) {
      const typed = el as HTMLElement & {
        showPopover?: () => void;
        matches(sel: string): boolean;
      };
      if (typeof typed.showPopover === 'function') {
        try {
          if (!typed.matches(':popover-open')) typed.showPopover();
        } catch {
          // Swallow — either a race (popover already opening) or
          // a feature-detect gap (JSDOM etc.). The signal-less
          // tooltip doesn't need reconciliation; the next
          // interaction will retry cleanly.
        }
      }
    } else if (!this.fallbackHandle) {
      // CDK-fallback path: anchor the pooled element to the host
      // via the overlay service. `lightDismiss: false` because
      // tooltips dismiss via their own mouse/focus handlers —
      // outside-click dismissal would fight mouseleave.
      // `passthroughPointerEvents: true` keeps the underlying
      // page interactive; the tooltip itself is non-interactive
      // (CSS `pointer-events: none` in the stylesheet).
      this.fallbackHandle = this.fallback.openAt(
        this.host.nativeElement,
        el,
        this.twTooltipPlacement(),
        { lightDismiss: false, passthroughPointerEvents: true },
      );
    }
    this.state = 'visible';
  }

  private hideNow(): void {
    const el = this.tooltipEl;
    if (!el) {
      this.state = 'idle';
      return;
    }
    if (this.useNative) {
      const typed = el as HTMLElement & {
        hidePopover?: () => void;
        matches(sel: string): boolean;
      };
      if (typeof typed.hidePopover === 'function') {
        try {
          if (typed.matches(':popover-open')) typed.hidePopover();
        } catch {
          /* noop */
        }
      }
    } else if (this.fallbackHandle) {
      this.fallbackHandle.dispose();
      this.fallbackHandle = null;
    }
    this.state = 'idle';
  }

  private hideImmediate(): void {
    this.clearShowTimer();
    this.clearHideTimer();
    this.hideNow();
  }

  private clearShowTimer(): void {
    if (this.showTimer !== null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private clearHideTimer(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  // ---------------------------------------------------------------
  // Pooled element management
  // ---------------------------------------------------------------

  private ensurePooledElement(): HTMLElement | null {
    if (this.tooltipEl) return this.tooltipEl;
    if (typeof document === 'undefined') return null;

    const el = document.createElement('div');
    el.className = 'tw-tooltip';
    el.id = this.tooltipId;
    el.setAttribute('role', 'tooltip');

    // Prefer `popover="hint"` where supported. The base `[popover]`
    // API is a prerequisite; when the hint variant is missing
    // (feature-detected via Wave 1's helper), fall back to
    // `popover="auto"`. Native "hint" group-dismiss behaviour is
    // not available on the `auto` path — a visible but acceptable
    // difference for older browsers.
    if (supportsPopoverHint()) {
      el.setAttribute('popover', 'hint');
    } else {
      // `popover=""` equals `popover="auto"` per spec — using the
      // explicit value reads clearer for anyone inspecting the DOM.
      el.setAttribute('popover', 'auto');
    }

    el.setAttribute('data-placement', this.twTooltipPlacement());
    // `position-anchor` is set on the tooltip; `anchor-name`
    // is set on the host in `ngOnInit`.
    el.style.setProperty('position-anchor', this.anchorName);
    el.textContent = this.twTooltip();

    document.body.appendChild(el);
    this.tooltipEl = el;
    return el;
  }

  private destroyPooledElement(): void {
    // If a CDK fallback is holding a reference to the pooled
    // element, dispose it first so the overlay pane tears down
    // cleanly before the node is removed.
    if (this.fallbackHandle) {
      this.fallbackHandle.dispose();
      this.fallbackHandle = null;
    }
    const el = this.tooltipEl;
    if (!el) return;
    try {
      el.remove();
    } catch {
      /* noop */
    }
    this.tooltipEl = null;
  }

  // ---------------------------------------------------------------
  // One-shot stylesheet injection
  // ---------------------------------------------------------------

  private static ensureStylesInjected(): void {
    if (styleInjected) return;
    if (typeof document === 'undefined') return;
    const style = document.createElement('style');
    style.setAttribute('data-tw-tooltip-styles', '');
    style.textContent = TW_TOOLTIP_STYLESHEET;
    document.head.appendChild(style);
    styleInjected = true;
  }
}
